import { useCallback, useContext } from 'react';
import { CommsContext } from '../context';
import { type BookingKind, bookingHref, isRecordId } from './dispatchLink';
import { useDashboardOrigin } from './useDashboardOrigin';

/**
 * Where pressing a booking's number lands — the decision JobLink draws, as data.
 *
 * JobLink is the way to draw one. This is for the place that cannot: a row that
 * is itself a single button (ConversationList), which may hold no second
 * control, and so presses the number by delegation instead.
 *
 *   host — framed by a dashboard that handles OPEN_JOB: ask it to open the
 *          booking in its own router. Only for an id it can address exactly.
 *   href — otherwise, wherever a dashboard address is known: the booking's own
 *          route there.
 *   both null — nowhere to send it, and the number is just text.
 */
export interface JobTarget {
  host: (() => void) | null;
  href: string | null;
}

/** One resolver for a whole list, so a page of rows reads the context once. */
export function useJobTargets(): (kind: BookingKind, id: string | number | null | undefined) => JobTarget {
  // Read without requiring it, as JobLink does: a booking component drawn
  // outside the module gets a link, or text, rather than a crash.
  const comms = useContext(CommsContext);
  const embedded = comms?.source.embedded ?? false;
  const openJob = comms?.source.openJob;
  const origin = useDashboardOrigin(embedded);
  return useCallback(
    (kind, id) => {
      const dispatchId = String(id ?? '').trim();
      // A quote is a job that has not been booked: the same page.
      if (openJob && isRecordId(dispatchId)) {
        const job = { kind: kind === 'draft' ? ('draft' as const) : ('job' as const), id: Number(dispatchId) };
        return { host: () => openJob(job), href: null };
      }
      return { host: null, href: bookingHref(origin, kind, dispatchId) };
    },
    [openJob, origin],
  );
}

export function useJobTarget(kind: BookingKind, id: string | number | null | undefined): JobTarget {
  return useJobTargets()(kind, id);
}

/**
 * The target as one press: the dashboard's own navigation, else the route in a
 * new tab, else nothing at all — for a caller with no link to put an href on.
 */
export function pressJob(target: JobTarget): (() => void) | null {
  if (target.host) return target.host;
  const { href } = target;
  if (!href) return null;
  return () => {
    window.open(href, '_blank', 'noopener,noreferrer');
  };
}
